// Interfaces
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    username: string;
    name: string;
    role: string;
  };
}

export interface AuthError {
  message: string;
  code?: string;
  status?: number;
}

export interface ChangePasswordRequest {
  username: string;
  currentPassword: string;
  newPassword: string;
}

export interface UserRoles {
  roles: string[];
}

export type UserRole = 'ROLE_ADMIN' | 'ROLE_DEALER' | 'ROLE_USER' | 'ROLE_MASTER';

// API URL
const API_URL = process.env.REACT_APP_API_URL;

/**
 * Decode JWT token to extract payload
 * @param token - JWT token
 * @returns Decoded payload or null if invalid
 */
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar token JWT:', error);
    return null;
  }
};

/**
 * Service responsible for authentication operations
 */
export const authService = {
  /**
   * Login user with username and password
   * @param username - User's username
   * @param password - User's password
   * @returns Promise with login response containing token and user data
   */
  login: async (username: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' // Empty bearer token for initial login
        },
        body: JSON.stringify({ username, password })
      });

      // Verificar status 428 (Precondition Required) - Senha precisa ser alterada
      // A mensagem diz o motivo: primeiro acesso/senha redefinida ou senha vencida
      if (response.status === 428) {
        const body = await response.json().catch(() => ({}));
        const error: AuthError = {
          message: body.message || 'É necessário alterar sua senha',
          status: 428
        };
        throw error;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Não foi possível fazer login. Verifique suas credenciais e tente novamente.');
      }

      const data = await response.json();
      
      // Store token in localStorage for future requests
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      
      return data;
    } catch (error) {
      console.error('Erro de login:', error);
      if (error instanceof Error) {
        throw error;
      } else if ((error as AuthError).status === 428) {
        throw error;
      } else {
        throw new Error('Não foi possível fazer login. Por favor, tente novamente.');
      }
    }
  },

  /**
   * Logout user by removing stored tokens
   */
  logout: (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    sessionStorage.removeItem('masterToken');
  },

  /**
   * Check if user is authenticated
   * @returns Boolean indicating if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('authToken');
  },

  /**
   * Get stored authentication token
   * @returns The authentication token or null if not authenticated
   */
  getToken: (): string | null => {
    return localStorage.getItem('authToken');
  },

  /**
   * Get current user data
   * @returns User data object or null if not authenticated
   */
  getCurrentUser: () => {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  },

  /**
   * Get user roles from JWT token
   * @returns Array of user roles or empty array if not authenticated
   */
  getUserRoles: (): string[] => {
    const token = authService.getToken();
    if (!token) return [];
    
    const decoded = decodeJWT(token);
    return decoded?.roles || [];
  },

  /**
   * Check if user has specific role
   * @param role - Role to check
   * @returns Boolean indicating if user has the role
   */
  hasRole: (role: UserRole): boolean => {
    const userRoles = authService.getUserRoles();
    return userRoles.includes(role);
  },

  /**
   * Check if user has any of the specified roles
   * @param roles - Array of roles to check
   * @returns Boolean indicating if user has any of the roles
   */
  hasAnyRole: (roles: UserRole[]): boolean => {
    const userRoles = authService.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  },

  /**
   * Check if user is admin
   * @returns Boolean indicating if user is admin
   */
  isAdmin: (): boolean => {
    return authService.hasRole('ROLE_ADMIN');
  },

  /**
   * Check if user is partner
   * @returns Boolean indicating if user is partner
   */
  isPartner: (): boolean => {
    return authService.hasRole('ROLE_DEALER');
  },

  /**
   * Check if user is regular user
   * @returns Boolean indicating if user is regular user
   */
  isUser: (): boolean => {
    return authService.hasRole('ROLE_USER');
  },

  /**
   * Check if user is master (can impersonate any active dealer)
   * @returns Boolean indicating if user is master
   */
  isMaster: (): boolean => {
    return authService.hasRole('ROLE_MASTER');
  },

  /**
   * Check if the current session token is an impersonation token
   * (i.e. a master user acting as a dealer)
   * @returns Boolean indicating if the current session is impersonating a dealer
   */
  isImpersonating: (): boolean => {
    const token = authService.getToken();
    if (!token) return false;

    const decoded = decodeJWT(token);
    return !!decoded?.impersonatedBy;
  },

  /**
   * Store the token obtained from the master "impersonate dealer" endpoint,
   * replacing the current session as if the dealer had logged in directly.
   * The master's own token is stashed in sessionStorage so it can be restored
   * later by returnToMaster() (e.g. when switching to another dealer).
   * @param token - Impersonation token returned by POST /master/impersonate/{dealerId}
   * @param dealer - Basic dealer info to keep userData reasonably up to date
   */
  setImpersonationSession: (token: string, dealer: { id: string; name: string; cnpj: string }): void => {
    const currentToken = authService.getToken();
    if (currentToken && !authService.isImpersonating()) {
      sessionStorage.setItem('masterToken', currentToken);
    }

    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify({
      id: dealer.id,
      username: dealer.cnpj,
      name: dealer.name,
      role: 'ROLE_USER'
    }));
  },

  /**
   * Restore the master's own token (stashed by setImpersonationSession), so the
   * master user can pick a different dealer to impersonate.
   * @returns Boolean indicating whether a stashed master token was found and restored
   */
  returnToMaster: (): boolean => {
    const masterToken = sessionStorage.getItem('masterToken');
    if (!masterToken) return false;

    localStorage.setItem('authToken', masterToken);
    localStorage.removeItem('userData');
    return true;
  },

  /**
   * Change user password
   * @param username - User's username
   * @param currentPassword - User's current password
   * @param newPassword - User's new password
   * @returns Promise with success message
   */
  changePassword: async (username: string, currentPassword: string, newPassword: string): Promise<any> => {
    try {
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, currentPassword, newPassword })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Não foi possível alterar a senha. Verifique se a senha atual está correta.');
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Não foi possível alterar a senha. Por favor, tente novamente.');
      }
    }
  }
};